/**
 * CitiesPanel — manage the shared outreach city list (Users admin → Cities tab).
 *
 * Cities are the options staff pick from when logging an outreach day.
 * Adding, renaming and deleting cities is admin-only (the server rejects
 * staff-PIN sessions); renaming cascades to every outreach entry that uses
 * the city. Staff still pick cities on their own calendars — only editing
 * the list is locked to admin.
 *
 * Auth: exchanges the parent badge admin token for a pp token, same as
 * UsersPanel / StaffPanel.
 */
import { useCallback, useEffect, useState } from "react";
import { Loader2, Pencil, Plus, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

export default function CitiesPanel({ badgeToken }) {
  const [ppToken, setPpToken] = useState(null);
  const [exchangeError, setExchangeError] = useState("");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newCity, setNewCity] = useState("");
  const [renameFrom, setRenameFrom] = useState("");
  const [renameTo, setRenameTo] = useState("");
  const [busy, setBusy] = useState(false);

  // Exchange badge admin token for a pp admin token
  useEffect(() => {
    if (!badgeToken) return;
    (async () => {
      try {
        const res = await fetch(`${API}/pp/auth/exchange-badge-token`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ badge_token: badgeToken }),
        });
        if (!res.ok) {
          const b = await res.json().catch(() => ({}));
          setExchangeError(b.detail || "Session exchange failed");
          return;
        }
        const d = await res.json();
        setPpToken(d.token);
      } catch (_) {
        setExchangeError("Network error during session exchange");
      }
    })();
  }, [badgeToken]);

  const load = useCallback(async () => {
    if (!ppToken) return;
    setLoading(true);
    try {
      const res = await fetch(`${API}/consultations/outreach-cities`, {
        headers: { "X-Auth-Token": ppToken },
      });
      if (!res.ok) throw new Error("load failed");
      const d = await res.json();
      setItems(Array.isArray(d?.cities) ? d.cities : []);
    } catch {
      toast.error("Couldn't load the city list");
    } finally {
      setLoading(false);
    }
  }, [ppToken]);

  useEffect(() => { load(); }, [load]);

  const call = async (method, path, body) => {
    const res = await fetch(`${API}/consultations${path}`, {
      method,
      headers: { "Content-Type": "application/json", "X-Auth-Token": ppToken },
      ...(body ? { body: JSON.stringify(body) } : {}),
    });
    const d = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(d.detail || "save failed");
    return d;
  };

  const addCity = async () => {
    const name = newCity.trim();
    if (!name || busy) return;
    setBusy(true);
    try {
      await call("POST", "/outreach-cities", { name });
      setNewCity("");
      toast.success(`"${name}" added.`);
      await load();
    } catch (e) {
      toast.error(e?.message || "Failed to add city.");
    } finally {
      setBusy(false);
    }
  };

  const doRename = async () => {
    const from = renameFrom.trim();
    const to = renameTo.trim();
    if (!to || to === from || busy) { setRenameFrom(""); return; }
    setBusy(true);
    try {
      const d = await call("PATCH", `/outreach-cities/${encodeURIComponent(from)}`, { new_name: to });
      setRenameFrom("");
      toast.success(`Renamed to "${to}". Updated ${d?.renamed_entries || 0} entr${(d?.renamed_entries || 0) === 1 ? "y" : "ies"}.`);
      await load();
    } catch (e) {
      toast.error(e?.message || "Failed to rename city.");
    } finally {
      setBusy(false);
    }
  };

  const removeCity = async (name) => {
    if (busy) return;
    if (!window.confirm(`Remove "${name}" from the city list? Existing outreach entries will keep their city label until you re-save them.`)) return;
    setBusy(true);
    try {
      await call("DELETE", `/outreach-cities/${encodeURIComponent(name)}`);
      toast.success(`"${name}" removed.`);
      await load();
    } catch (e) {
      toast.error(e?.message || "Failed to remove city.");
    } finally {
      setBusy(false);
    }
  };

  if (exchangeError) {
    return <p className="text-sm text-red-600" data-testid="cities-exchange-error">{exchangeError}</p>;
  }

  return (
    <div data-testid="cities-panel" className="max-w-xl">
      <p className="text-xs text-slate-500 mb-4">
        The city options staff pick from when logging an outreach day. Renaming a city updates
        every outreach entry that uses it. Staff can pick cities on their calendars — only
        editing this list is admin-only.
      </p>

      <div className="rounded-lg border border-slate-200 bg-white p-3 space-y-2" data-testid="cities-list">
        {loading && <p className="text-sm text-slate-400 py-6 text-center">Loading…</p>}
        {!loading && items.length === 0 && (
          <p className="text-sm text-slate-400 py-6 text-center">No cities yet — add one below.</p>
        )}
        {!loading && items.map((c) => (
          <div key={c} className="flex items-center gap-2" data-testid={`city-row-${c}`}>
            {renameFrom === c ? (
              <>
                <Input
                  autoFocus
                  value={renameTo}
                  onChange={(e) => setRenameTo(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter") doRename(); }}
                  data-testid="city-rename-input"
                  className="h-9 flex-1"
                />
                <Button
                  onClick={doRename}
                  disabled={busy || !renameTo.trim() || renameTo.trim() === c}
                  data-testid="city-rename-save"
                  className="h-9 bg-brand-teal text-white"
                >
                  Save
                </Button>
                <Button variant="outline" onClick={() => setRenameFrom("")} className="h-9">
                  Cancel
                </Button>
              </>
            ) : (
              <>
                <span className="flex-1 text-sm text-slate-800 truncate">{c}</span>
                <button
                  type="button"
                  onClick={() => { setRenameFrom(c); setRenameTo(c); }}
                  disabled={busy}
                  data-testid={`city-edit-${c}`}
                  title={`Rename "${c}"`}
                  className="p-2 rounded-md border border-slate-200 text-brand-teal hover:bg-slate-50 disabled:opacity-40"
                >
                  <Pencil className="w-3.5 h-3.5" />
                </button>
                <button
                  type="button"
                  onClick={() => removeCity(c)}
                  disabled={busy}
                  data-testid={`city-delete-${c}`}
                  title={`Delete "${c}"`}
                  className="p-2 rounded-md border border-slate-200 text-red-700 hover:bg-slate-50 disabled:opacity-40"
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </>
            )}
          </div>
        ))}
        <div className="flex gap-2 pt-2 border-t border-dashed border-slate-200">
          <Input
            value={newCity}
            onChange={(e) => setNewCity(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") addCity(); }}
            placeholder="Add a city"
            data-testid="city-new-input"
            className="h-9 flex-1"
          />
          <Button
            onClick={addCity}
            disabled={busy || !newCity.trim()}
            data-testid="city-add-btn"
            className="h-9 bg-brand-teal text-white"
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Plus className="w-4 h-4 mr-1" /> Add</>}
          </Button>
        </div>
      </div>
    </div>
  );
}
